import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Operations.css';

function OperationsLanding() {
  const [sku, setSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sku.trim()) return;

    setLoading(true);
    setError('');

    try {
      // Check if item exists in database
      const response = await axios.get(`/api/item/${sku}`);

      if (response.data) {
        // Item found, navigate to operations menu
        navigate(`/ops/${sku}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // Item not found, try to lookup in NetSuite
        try {
          const lookupResponse = await axios.get(`/api/lookup/${sku}`);
          if (lookupResponse.data.item) {
            // Item found and added to database
            navigate(`/ops/${sku}`);
          } else {
            setError(`Item ${sku} not found in NetSuite`);
            setSku('');
            inputRef.current?.focus();
          }
        } catch (lookupErr) {
          setError(`Failed to lookup item: ${lookupErr.response?.data?.error || 'Unknown error'}`);
          setSku('');
          inputRef.current?.focus();
        }
      } else {
        setError(`Error: ${err.response?.data?.error || 'Failed to fetch item'}`);
        setSku('');
        inputRef.current?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ops-container">
      <div className="ops-landing">
        <div className="ops-landing-header">
          <h1>Warehouse Operations</h1>
          <p className="subtitle">Enter an item SKU to manage inventory</p>
        </div>

        <form onSubmit={handleSubmit} className="sku-entry-form">
          <div className="input-group large">
            <label htmlFor="sku-input">Item SKU</label>
            <input
              ref={inputRef}
              id="sku-input"
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value.toUpperCase())}
              placeholder="Scan or enter SKU..."
              disabled={loading}
              autoComplete="off"
              autoFocus
              className="sku-input-large"
            />
          </div>

          <button
            type="submit"
            disabled={!sku.trim() || loading}
            className="submit-button-large"
          >
            {loading ? 'Searching...' : 'Go to Operations'}
          </button>
        </form>

        {error && (
          <div className="message-box error">{error}</div>
        )}

        <div className="quick-links">
          <h3>Quick Actions</h3>
          <div className="quick-links-grid">
            <button
              onClick={() => navigate('/production/tanks')}
              className="quick-link"
            >
              <span className="quick-link-icon">🏭</span>
              <span className="quick-link-label">Tank Dashboard</span>
            </button>

            <button
              onClick={() => navigate('/production/dashboard')}
              className="quick-link"
            >
              <span className="quick-link-icon">📊</span>
              <span className="quick-link-label">Production Dashboard</span>
            </button>

            <button
              onClick={() => navigate('/ops/RW10101CAB/singles-convert')}
              className="quick-link"
            >
              <span className="quick-link-icon">📦</span>
              <span className="quick-link-label">Singles to Cases</span>
            </button>

            <button
              onClick={() => navigate('/ops/RW20101CAB/convert')}
              className="quick-link"
            >
              <span className="quick-link-icon">📤</span>
              <span className="quick-link-label">Cases to Singles</span>
            </button>

            <button
              onClick={() => navigate('/low-stock')}
              className="quick-link"
            >
              <span className="quick-link-icon">⚠️</span>
              <span className="quick-link-label">Low Stock</span>
            </button>

            <button
              onClick={() => navigate('/slow-movers')}
              className="quick-link"
            >
              <span className="quick-link-icon">📉</span>
              <span className="quick-link-label">Slow Movers</span>
            </button>

            <button
              onClick={() => navigate('/stock-by-location')}
              className="quick-link"
            >
              <span className="quick-link-icon">📍</span>
              <span className="quick-link-label">Stock by Location</span>
            </button>

            <button
              onClick={() => navigate('/oos-risk')}
              className="quick-link"
            >
              <span className="quick-link-icon">🚨</span>
              <span className="quick-link-label">Out-of-Stock Risk</span>
            </button>

            <button
              onClick={() => navigate('/reorder-suggestions')}
              className="quick-link"
            >
              <span className="quick-link-icon">🛒</span>
              <span className="quick-link-label">Reorder Suggestions</span>
            </button>

            <button
              onClick={() => navigate('/labels')}
              className="quick-link"
            >
              <span className="quick-link-icon">🏷️</span>
              <span className="quick-link-label">Label Printing</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OperationsLanding;